import { useCallback, useEffect, useMemo, useState } from 'react'
import { buildGroupMessages, hiddenKeySet } from '../lib/groupMessages'
import {
  fetchGroupActivity,
  fetchGroupAnnouncements,
  fetchGroupMessageHides,
  setGroupMessageHidden,
} from '../services/groupService'

const FEED_LIMIT = 50

// Loads the group's Messages feed and the viewer's own hidden items. Each of
// the three reads is non-critical: one failing still shows the other two.
export default function useGroupMessages(groupId) {
  const [announcements, setAnnouncements] = useState([])
  const [activity, setActivity] = useState([])
  const [hides, setHides] = useState([])
  const [loading, setLoading] = useState(true)

  const reload = useCallback(async () => {
    if (!groupId) return
    const [ann, act, hid] = await Promise.allSettled([
      fetchGroupAnnouncements({ groupId, limit: FEED_LIMIT }),
      fetchGroupActivity({ groupId, limit: FEED_LIMIT }),
      fetchGroupMessageHides({ groupId }),
    ])
    if (ann.status === 'fulfilled' && ann.value) setAnnouncements(ann.value)
    if (act.status === 'fulfilled' && act.value) setActivity(act.value)
    if (hid.status === 'fulfilled' && hid.value) setHides(hid.value)
    setLoading(false)
  }, [groupId])

  useEffect(() => {
    setLoading(true)
    reload()
  }, [reload])

  const items = useMemo(() => buildGroupMessages({ announcements, activity }), [announcements, activity])
  const hiddenKeys = useMemo(() => hiddenKeySet(hides), [hides])

  // Optimistic: the tick moves the item at once, and rolls back on error.
  const setHidden = useCallback(async (item, hidden) => {
    const entry = { item_kind: item.kind, item_id: String(item.id) }
    const without = (list) => list.filter(
      (h) => !(h.item_kind === entry.item_kind && h.item_id === entry.item_id))
    const previous = hides
    setHides(hidden ? [...without(hides), entry] : without(hides))
    try {
      await setGroupMessageHidden({ groupId, kind: item.kind, itemId: item.id, hidden })
    } catch (err) {
      setHides(previous)
      throw err
    }
  }, [groupId, hides])

  return { items, hiddenKeys, loading, reload, setHidden }
}
